import React, { useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CollectionType } from '../../data-layer/collections/service';
import { CollectionCard } from '../../ui/users/CollectionCard';
import { CollectionsGrid } from '../../ui/users/CollectionsGrid';
import { isBlogger, isBloggerBusiness, isBusinessAdmin } from '../../utils/accessService';
import { getIsAuthenticated } from '../../utils/getIsAuthenticated';
import { useIsMobile } from '../../utils/getIsMobile';
import { useExperimentFlag } from '../../utils/useExperimentFlag';
import { useTranslation } from '../../utils/useTranslation';
import { useCollectionsController } from '../users/useCollectionsController';
import { useCurrentUser } from '../users/useCurrentUser';
import { withCollectionCallbacks } from '../users/withCollectionCallbacks';
import { useBusinessModel } from './useBusinessModel';

const EnhancedCollectionCard = withCollectionCallbacks(CollectionCard);

export const makeBusinessCollectionsTab = () => () => {
    const params = useParams();
    const businessId = params.businessId!;
    const business = useBusinessModel({ businessId });
    const currentUser = useCurrentUser();
    const isLoggedIn = getIsAuthenticated();
    const [query, setQuery] = useSearchParams();
    const t = useTranslation();
    const isBloggerOwner =
        isBusinessAdmin(business?.id, currentUser) && isBlogger(currentUser?.role);

    const isUserDataReady = !isLoggedIn || currentUser;
    const shouldFetchCollections = Boolean(isUserDataReady && isBloggerBusiness(business?.type));

    const { collections, savedCollections, createdCollections, isLoaded } =
        useCollectionsController({
            businessId: isBloggerOwner ? undefined : business?.id,
            requestCondition: shouldFetchCollections,
        });

    const collectionType = (
        query.get('collectionType') === CollectionType.SAVED && isBloggerOwner
            ? CollectionType.SAVED
            : CollectionType.CREATED
    ) as CollectionType;

    useEffect(() => {
        const needChangeQuery = Boolean(
            isLoaded &&
                !savedCollections.length &&
                createdCollections.length &&
                createdCollections[0] &&
                collectionType !== CollectionType.CREATED,
        );

        if (needChangeQuery) {
            setQuery({ collectionType: CollectionType.CREATED }, { replace: true });
        }
    }, [
        collectionType,
        createdCollections,
        createdCollections.length,
        isLoaded,
        savedCollections.length,
        setQuery,
    ]);

    const currentCollection = useMemo(() => {
        if (!isBloggerOwner) {
            return collections;
        }

        return collectionType === CollectionType.SAVED ? savedCollections : createdCollections;
    }, [collectionType, collections, createdCollections, isBloggerOwner, savedCollections]);

    const isMobile = useIsMobile();
    const isWideProfileExp = Number(useExperimentFlag('rythm_disable_wide_profile')) !== 1;
    const isWideProfile = isWideProfileExp && isMobile === false;

    return (
        <>
            <CollectionsGrid
                borderRadius={'0 0 24px 24px'}
                mr={isWideProfile ? 2 : 0}
                columnsCount={isWideProfile ? 5 : 3}
                stubMessage={isBloggerOwner ? undefined : t('Список коллекций пуст')}
                collections={currentCollection}
                isLoading={!isLoaded}
                renderItem={(index) => (
                    <EnhancedCollectionCard
                        key={currentCollection[index].id}
                        collection={currentCollection[index]}
                        isSaved={currentCollection[index].isSaved}
                        isOwner={
                            isBloggerOwner
                                ? currentCollection[index]?.modifiable ||
                                  currentCollection[index]?.defaultCollection
                                : false
                        }
                        isOwnerDataHidden={!isBloggerOwner || !currentCollection[index].isSaved}
                        columnsCount={isWideProfile ? 5 : 3}
                    />
                )}
            />
        </>
    );
};
